var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { useLiFi, useWallet } from '../providers';
import { formatTokenAmount } from '../utils';
const defaultRefetchInterval = 60000;
export const useTokenBalance = (token, accountAddress) => {
    const lifi = useLiFi();
    const { account } = useWallet();
    const queryClient = useQueryClient();
    const walletAddress = accountAddress !== null && accountAddress !== void 0 ? accountAddress : account.address;
    const getTokenBalancesWithRetry = useCallback((accountAddress, tokens, depth = 0) => __awaiter(void 0, void 0, void 0, function* () {
        try {
            const tokenBalances = yield lifi.getTokenBalances(accountAddress, tokens);
            if (!tokenBalances.every((token) => token.blockNumber)) {
                if (depth > 10) {
                    console.warn('Token balance backoff depth exceeded.');
                    return undefined;
                }
                yield new Promise((resolve) => {
                    setTimeout(resolve, Math.pow(1.5, depth) * 100);
                });
                return getTokenBalancesWithRetry(accountAddress, tokens, depth + 1);
            }
            return tokenBalances;
        }
        catch (error) {
            //
        }
    }), [lifi]);
    const tokenBalanceQueryKey = useMemo(() => ['token-balance', walletAddress, token === null || token === void 0 ? void 0 : token.chainId, token === null || token === void 0 ? void 0 : token.address], [token === null || token === void 0 ? void 0 : token.address, token === null || token === void 0 ? void 0 : token.chainId, walletAddress]);
    const { data, isLoading, refetch } = useQuery(tokenBalanceQueryKey, ({ queryKey: [, accountAddress] }) => __awaiter(void 0, void 0, void 0, function* () {
        var _a;
        const cachedToken = (_a = queryClient
            .getQueryData([
            'token-balances',
            accountAddress,
            token.chainId,
        ])) === null || _a === void 0 ? void 0 : _a.find((t) => t.address === token.address);
        if (cachedToken) {
            return cachedToken;
        }
        const tokenBalances = yield getTokenBalancesWithRetry(accountAddress, [token]);
        if (!(tokenBalances === null || tokenBalances === void 0 ? void 0 : tokenBalances.length)) {
            throw Error('Could not get tokens balance.');
        }
        const cachedTokenAmount = queryClient.getQueryData(tokenBalanceQueryKey);
        const formattedAmount = formatTokenAmount(tokenBalances[0].amount);
        if ((cachedTokenAmount === null || cachedTokenAmount === void 0 ? void 0 : cachedTokenAmount.amount) !== formattedAmount) {
            queryClient.setQueryDefaults(tokenBalanceQueryKey, {
                refetchInterval: defaultRefetchInterval,
                staleTime: defaultRefetchInterval,
            });
        }
        queryClient.setQueryData(['token-balances', accountAddress, token.chainId], (data) => {
            if (data) {
                const index = data.findIndex((dataToken) => dataToken.address === token.address);
                data[index] = Object.assign(Object.assign({}, data[index]), { amount: formattedAmount });
            }
            return data;
        });
        return Object.assign(Object.assign({}, tokenBalances[0]), { amount: formattedAmount });
    }), {
        enabled: Boolean(walletAddress && token),
        refetchInterval: defaultRefetchInterval,
        staleTime: defaultRefetchInterval,
    });
    const refetchAllBalances = () => {
        queryClient.refetchQueries([
            'token-balances',
            token === null || token === void 0 ? void 0 : token.chainId,
            accountAddress,
        ]);
    };
    const refetchNewBalance = useCallback(() => {
        queryClient.setQueryDefaults(tokenBalanceQueryKey, {
            refetchInterval: 500,
            staleTime: 500,
        });
    }, [queryClient, tokenBalanceQueryKey]);
    return {
        token: data,
        isLoading,
        refetch,
        refetchNewBalance,
        refetchAllBalances,
        getTokenBalancesWithRetry,
    };
};
